import React, { useState, useEffect } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { MdEdit, MdDelete } from "react-icons/md";
import { IoMdAdd } from "react-icons/io";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-toastify/dist/ReactToastify.css";

const localizer = momentLocalizer(moment);
const baseUrl = process.env.REACT_APP_API_BASE_URL;

const SchoolCalendar = () => {
  const userData = sessionStorage.getItem("user");
  const userObj = userData ? JSON.parse(userData) : {};
  const readOnlyRoles = ["Class Teacher", "Teacher", "Class Incharge", "School Admin"];
  const canSubmit = !readOnlyRoles.includes(userObj.role_name?.trim());

  const [events, setEvents] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [view, setView] = useState("month");
  const [date, setDate] = useState(new Date());
  const [showModal, setShowModal] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [activeTab, setActiveTab] = useState("all");


  const [form, setForm] = useState({
    type: "EVENT",
    event_id: "",
    holiday_id: "",
    title: "",
    event_date: "",
    start_time: "",
    end_time: "",
    academic_year_id: userObj.academic_year_id || "",
    school_id: userObj.school_id || "",
  });

  const [academicYearOptions, setAcademicYearOptions] = useState([]);
  const [minDate, setMinDate] = useState("");

  useEffect(() => {
    fetchEvents();
    const today = moment().format("YYYY-MM-DD");
    // setMinDate(today);
    fetchAcademicYearOptions();
    fetchHolidays();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.post(`${baseUrl}/events/`, {
        action: "READ",
        school_id: userObj.school_id || "",
      });
      const formattedEvents =
        response.data?.map((event) => ({
          ...event,
          id: event.event_id,
          start: new Date(event.event_date),
          end: new Date(event.event_date),
        })) || [];
      setEvents(formattedEvents);
    } catch (error) {
      console.error("Error fetching events:", error);
    }
  };

  const fetchAcademicYearOptions = async () => {
    try {
      const response = await axios.post(`${baseUrl}/AcademicYear/`, {
        action: "CURRENTREAD",
        school_id: userObj.school_id || "",
      });
      setAcademicYearOptions(response.data || []);
    } catch (error) {
      console.error("Error fetching academic years:", error);
    }
  };

  const fetchHolidays = async () => {
    try {
      const response = await axios.post(`${baseUrl}/holiday/`, {
        action: "READ",
        school_id: userObj.school_id || "",
      });
      setHolidays(response.data || []);
    } catch (error) {
      console.error("Error fetching Holidays:", error);
    }
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setForm((prev) => ({ ...prev, [id]: value }));
  };

  // ===== unified submit for Event / Holiday =====
  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};

    if (!form.title?.trim()) errors.title = `${form.type === "HOLIDAY" ? "Holiday" : "Event"} name is required.`;
    if (!form.event_date) errors.event_date = `${form.type === "HOLIDAY" ? "Holiday" : "Event"} date is required.`;

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      if (form.type === "HOLIDAY") {
        const payload = {
          holiday_name: form.title,
          holiday_date: form.event_date,
          academic_year_id: form.academic_year_id || userObj.academic_year_id || "",
          school_id: form.school_id || userObj.school_id || "",
          action: form.holiday_id ? "UPDATE" : "CREATE",
          ...(form.holiday_id ? { holiday_id: form.holiday_id } : {}),
        };

        await axios.post(`${baseUrl}/holiday/`, payload);
        toast.success(form.holiday_id ? "Holiday updated successfully" : "Holiday added successfully");
        fetchHolidays();
      } else {
        const payload = {
          title: form.title,
          event_date: form.event_date,
          start_time: form.start_time,
          end_time: form.end_time,
          academic_year_id: form.academic_year_id || userObj.academic_year_id || "",
          school_id: form.school_id || userObj.school_id || "",
          action: form.event_id ? "UPDATE" : "CREATE",
          ...(form.event_id ? { event_id: form.event_id } : {}),
        };

        await axios.post(`${baseUrl}/events/`, payload, { headers: { "Content-Type": "application/json" } });
        toast.success(form.event_id ? "Event updated successfully" : "Event added successfully");
        fetchEvents();
      }

      // reset and close
      setForm({
        type: "EVENT",
        event_id: "",
        holiday_id: "",
        title: "",
        event_date: "",
        start_time: "",
        end_time: "",
        academic_year_id: userObj.academic_year_id || "",
        school_id: userObj.school_id || "",
      });
      setFormErrors({});
      setShowModal(false);
    } catch (error) {
      console.error("Error submitting:", error);
      toast.error(`Failed to submit ${form.type === "HOLIDAY" ? "holiday" : "event"}. Please try again.`);
    }
  };

  const handleDelete = async (eventId) => {
    try {
      await axios.post(`${baseUrl}/events/`, { action: "DELETE", event_id: eventId });
      window.alert("Are you sure you want to delete ?");
      toast.success("Event deleted successfully !");
      fetchEvents();
    } catch (error) {
      console.error("Error deleting event:", error);
      toast.error("Failed to delete event. Please try again.");
    }
  };

  const handleHolidayDelete = async (holidayId) => {
    const confirmed = window.confirm("Are you sure you want to delete this holiday?");
    if (!confirmed) return;
    try {
      await axios.post(`${baseUrl}/holiday/`, { action: "DELETE", holiday_id: holidayId });
      toast.success("Holiday deleted successfully!");
      fetchHolidays();
    } catch (error) {
      console.error("Error deleting holiday:", error);
      toast.error("Failed to delete holiday. Please try again.");
    }
  };

  // ===== open modal prefilled for Event edit =====
  const handleEdit = (event) => {
    setForm({
      type: "EVENT",
      event_id: event.event_id,
      holiday_id: "",
      title: event.title,
      event_date: moment(event.start).format("YYYY-MM-DD"),
      start_time: moment(event.start).format("HH:mm"),
      end_time: moment(event.end).format("HH:mm"),
      academic_year_id: event.academic_year_id || userObj.academic_year_id || "",
      school_id: userObj.school_id || "",
    });
    setFormErrors({});
    setShowModal(true);
  };

  // ===== open modal prefilled for Holiday edit =====
  const handleHolidayEdit = (holiday) => {
    setForm({
      type: "HOLIDAY",
      event_id: "",
      holiday_id: holiday.holiday_id,
      title: holiday.holiday_name,
      event_date: moment(holiday.holiday_date).format("YYYY-MM-DD"),
      start_time: "",
      end_time: "",
      academic_year_id: holiday.academic_year_id || userObj.academic_year_id || "",
      school_id: userObj.school_id || "",
    });
    setFormErrors({});
    setShowModal(true);
  };

  // calendar list mix
  const holidayEvents = holidays.map((holiday) => ({
    title: holiday.holiday_name || holiday.title,
    start: new Date(holiday.holiday_date || holiday.event_date),
    end: new Date(holiday.holiday_date || holiday.event_date),
    allDay: true,
    isHoliday: true,
  }));
  const combinedEvents = [...events, ...holidayEvents];

  const CustomToolbar = ({ label, onNavigate }) => {
    return (
      <div
        className="custom-toolbar"
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0px" }}
      >
        <h5>{label}</h5>
        <div style={{ display: "flex", gap: "10px" }}>
          <button className="arrowBtn" onClick={() => onNavigate("PREV")}>{"<"}</button>
          <button className="linkBtn" onClick={() => onNavigate("TODAY")}>Current</button>
          <button className="arrowBtn" onClick={() => onNavigate("NEXT")}>{">"}</button>
        </div>
      </div>
    );
  };

  const getFilteredEvents = () => {
    const currentMonthStart = moment().startOf("month");
    const currentMonthEnd = moment().endOf("month");
    if (activeTab === "current") {
      return events.filter((event) => {
        const eventDate = moment(event.start);
        return eventDate.isBetween(currentMonthStart, currentMonthEnd, "day", "[]");
      });
    }
    return events;
  };

  return (
    <div className="pageBody schoolCalander">
      <div className="calendar-container height100">
        <Row className="height100">
          <Col xs={12} md={8} className="height100">
            <div className="height100">
              <ToastContainer position="top-right" autoClose={2000} hideProgressBar />
              <div style={{ marginBottom: "10px", display: "flex", justifyContent: "flex-end" }}></div>

              <Calendar
                style={{ height: "87vh", minHeight: 550 }}
                className="w-100"
                localizer={localizer}
                events={combinedEvents}
                eventPropGetter={(event) =>
                  event.isHoliday ? { style: { backgroundColor: "#a5a2a1", color: "#fff" } } : {}
                }
                startAccessor="start"
                endAccessor="end"
                views={["month"]}
                defaultView="month"
                date={date}
                view={view}
                onNavigate={(newDate) => setDate(newDate)}
                onView={(newView) => setView(newView)}
                components={{ toolbar: CustomToolbar }}
                selectable
              />
            </div>
          </Col>

          <Col xs={12} md={4} className="p-0">
            <div className="eventsPart">
              <div className="contentAllignLR eventsPartHead">
                <div style={{ display: "flex", gap: "15px" }}>
                  <button className={`btn ${activeTab === "all" ? "active" : ""}`} onClick={() => setActiveTab("all")}>
                    All Events
                  </button>
                  <button
                    className={`btn ${activeTab === "current" ? "active" : ""}`}
                    onClick={() => setActiveTab("current")}
                  >
                    Current Events
                  </button>

                  <div style={{ position: "relative", display: "inline-block" }}>
                    <button
                      className={`btn ${activeTab === "holidays" ? "active" : ""}`}
                      onClick={() => {
                        setActiveTab("holidays");
                        fetchHolidays();
                      }}
                    >
                      Holidays
                    </button>
                  </div>

                </div>

                {/* Right side add opens modal configured for EVENT */}
                {canSubmit && (
                  <button
                    className="btn primaryBtn addBtn"
                    onClick={() => {
                      setForm({
                        type: "EVENT",
                        event_id: "",
                        holiday_id: "",
                        title: "",
                        event_date: "",
                        start_time: "",
                        end_time: "",
                        academic_year_id: userObj.academic_year_id || "",
                        school_id: userObj.school_id || "",
                      });
                      setFormErrors({});
                      setShowModal(true);
                    }}
                  >
                    <span><IoMdAdd /></span>
                  </button>
                )}
              </div>

              {/* List */}
              <div style={{ maxHeight: "710px", overflowY: "auto" }}>
                <ul style={{ listStyleType: "none", padding: 0, fontSize: "14px", marginBottom: "0" }}>
                  {activeTab === "holidays" ? (
                    holidays.length > 0 ? (
                      holidays.map((holiday, index) => (
                        <li
                          key={index}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "12px 15px",
                            backgroundColor: "#ffffff",
                            borderRadius: "8px",
                            marginBottom: "15px",
                            border: "1px solid #e0e0e0",
                          }}
                        >
                          <span style={{ color: "#333", fontWeight: "500" }}>
                            <strong>{holiday.holiday_name || holiday.title}</strong>
                            <br />
                            <span style={{ color: "#888", fontSize: "13px" }}>
                              {new Date(holiday.holiday_date || holiday.event_date).toLocaleDateString()}
                            </span>
                          </span>
                          <div style={{ display: "flex", alignItems: "center" }}>
                            {canSubmit && (
                              <>
                                <MdEdit
                                  style={{ cursor: "pointer", color: "#0d6efd", marginRight: "25px", fontSize: "18px" }}
                                  onClick={() => handleHolidayEdit(holiday)}
                                />
                                <MdDelete
                                  style={{ cursor: "pointer", color: "#e25c6e", fontSize: "18px" }}
                                  onClick={() => handleHolidayDelete(holiday.holiday_id)}
                                />
                              </>
                            )}
                          </div>
                        </li>
                      ))
                    ) : (
                      <p>No holidays available</p>
                    )
                  ) : getFilteredEvents()?.length > 0 ? (
                    getFilteredEvents().map((event, index) => (
                      <li
                        key={index}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "12px 15px",
                          backgroundColor: "#ffffff",
                          borderRadius: "8px",
                          marginBottom: "15px",
                          border: "1px solid " + "#e0e0e0",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#f1f1f1";
                          e.currentTarget.style.transform = "scale(1.02)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "#ffffff";
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      >
                        <span style={{ color: "#333", fontWeight: "500" }}>
                          <strong>{event.title}</strong>
                          <br />
                          <span style={{ color: "#888", fontSize: "13px" }}>
                            {event.start?.toLocaleDateString()}
                          </span>
                        </span>
                        {canSubmit && (
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <MdEdit
                              style={{ cursor: "pointer", color: "#0d6efd", marginRight: "15px", fontSize: "18px" }}
                              onClick={() => handleEdit(event)}
                            />
                            <MdDelete
                              style={{ cursor: "pointer", color: "#e25c6e", fontSize: "18px" }}
                              onClick={() => handleDelete(event.event_id)}
                            />
                          </div>
                        )}
                      </li>
                    ))
                  ) : (
                    <p>No events available</p>
                  )}
                </ul>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* ===== Single Modal for Event or Holiday ===== */}
      <Modal
        className="commonFilterModal"
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
      >
        <Modal.Header className="modalHeaderFixed" closeButton>
          <Modal.Title>
            {form.type === "HOLIDAY"
              ? (form.holiday_id ? "Update Holiday" : "Add Holiday")
              : (form.event_id ? "Update Event" : "Add Event")}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            {/* Type switcher */}
            <div className="commonInput">
              <Form.Group controlId="type">
                <Form.Label>Type</Form.Label>
                <Form.Select
                  value={form.type}
                  onChange={(e) => setForm((p) => ({ ...p, type: e.target.value }))}
                >
                  <option value="EVENT">Event</option>
                  <option value="HOLIDAY">Holiday</option>
                </Form.Select>
              </Form.Group>
            </div>

            <div className="commonInput">
              <Form.Group controlId="title">
                <Form.Label>
                  {form.type === "HOLIDAY" ? "Holiday Name" : "Event Name"}
                  <span className="requiredStar">*</span>
                </Form.Label>
                <Form.Control
                  required
                  type="text"
                  placeholder={`Enter ${form.type === "HOLIDAY" ? "Holiday" : "Event"} Name`}
                  value={form.title || ""}
                  onChange={handleInputChange}
                />
                {formErrors?.title && (
                  <Form.Text className="text-danger">{formErrors?.title}</Form.Text>
                )}
              </Form.Group>
            </div>

            <div className="commonInput">
              <Form.Group controlId="event_date">
                <Form.Label>{form.type === "HOLIDAY" ? "Holiday Date" : "Event Date"}</Form.Label>
                <Form.Control
                  type="date"
                  value={form.event_date || ""}
                  // min={minDate}
                  onChange={handleInputChange}
                />
                {formErrors?.event_date && (
                  <Form.Text className="text-danger">{formErrors?.event_date}</Form.Text>
                )}
              </Form.Group>
            </div>

            {/* Show time inputs only for Events */}
            {form.type === "EVENT" && (
              <>
                <div className="commonInput">
                  <Form.Group controlId="start_time">
                    <Form.Label>Start Time</Form.Label>
                    <Form.Control
                      type="time"
                      value={form.start_time || ""}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </div>

                <div className="commonInput">
                  <Form.Group controlId="end_time">
                    <Form.Label>End Time</Form.Label>
                    <Form.Control
                      type="time"
                      value={form.end_time || ""}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </div>
              </>
            )}

            <div className="commonInput">
              <Form.Group controlId="academic_year_id">
                <Form.Label>Academic Year</Form.Label>
                <Form.Control
                  as="select"
                  value={form.academic_year_id || ""}
                  onChange={handleInputChange}
                >
                  <option value="0" disabled hidden>
                    {userObj?.academic_year_name || "Select Academic Year"}
                  </option>
                  {academicYearOptions?.length > 0 ? (
                    academicYearOptions.map((year, index) => (
                      <option key={index} value={year.academic_year_id}>
                        {year.academic_year_name}
                      </option>
                    ))
                  ) : (
                    <option value="">No Academic Year Available</option>
                  )}
                </Form.Control>
              </Form.Group>
            </div>
          </Form>
        </Modal.Body>

        <Modal.Footer className="modalFooterFixed">
          <div>
            <Button
              type="button"
              variant="primary"
              className="btn-info clearBtn"
              onClick={() =>
                setForm((p) => ({
                  type: p.type,          // keep current type
                  event_id: "",
                  holiday_id: "",
                  title: "",
                  event_date: "",
                  start_time: "",
                  end_time: "",
                  academic_year_id: "",
                  school_id: userObj.school_id || "",
                }))
              }
            >
              Reset
            </Button>
          </div>
          <div>
            <Button
              type="button"
              variant="primary"
              className="btn-danger secondaryBtn me-2"
              onClick={() => {
                setShowModal(false);
                setForm({
                  type: "EVENT",
                  event_id: "",
                  holiday_id: "",
                  title: "",
                  event_date: "",
                  start_time: "",
                  end_time: "",
                  academic_year_id: "",
                  school_id: userObj.school_id || "",
                });
                setFormErrors({});
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              className="btn-success primaryBtn"
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SchoolCalendar;
